import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import {
  BundlesData,
  CategoriesData,
  GET_BUNDLES_FOR_VARIANT,
  GET_CATEGORIES,
  GET_PRODUCTS,
  ProductsData,
  PublicBundle,
  PublicCategory,
  PublicProduct,
} from '../graphql/public'
import { CURRY_CATEGORY, POLL_MS } from '../config/board'
import { activeVariants, productsInCategory } from './catalog'

// The one live read of the wall. Polled with `cache-and-network` so a sold-out mark
// published on the POS reaches the screen within POLL_MS.
//
// The rule that shapes this hook: **never blank the wall**. A failed poll must not
// take the menu off the screen, so the last good result is kept here and rendered
// with a "prices as of HH:MM" stamp until a poll succeeds again.

interface Kept<T> {
  data: T | undefined
  at: number | null
  failed: boolean
}

function useKeepLast<T>(data: T | undefined, error: unknown, skip: boolean): Kept<T> {
  const [kept, setKept] = useState<T | undefined>(undefined)
  const [at, setAt] = useState<number | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (skip) return
    if (error) {
      setFailed(true)
      return
    }
    if (data === undefined) return
    setKept(data)
    setAt(Date.now())
    setFailed(false)
  }, [data, error, skip])

  return { data: kept, at, failed }
}

export interface LiveCatalog {
  categories: PublicCategory[]
  products: PublicProduct[]
  bundle: PublicBundle | null
  /** When the data on screen was last confirmed by the API; null before the first result. */
  lastSuccessAt: number | null
  /** True when the newest poll failed — the screen is showing the previous result. */
  stale: boolean
}

export function useLiveCatalog(): LiveCatalog {
  const poll = { fetchPolicy: 'cache-and-network' as const, pollInterval: POLL_MS }

  const cats = useQuery<CategoriesData>(GET_CATEGORIES, poll)
  const prods = useQuery<ProductsData>(GET_PRODUCTS, poll)

  const keptCats = useKeepLast(cats.data, cats.error, false)
  const keptProds = useKeepLast(prods.data, prods.error, false)

  // `errorPolicy: 'all'` can hand back a partial result, so a field is only trusted
  // when it is actually a list — a half-answered poll must not take the wall down.
  const products = useMemo(() => {
    const list = keptProds.data ? keptProds.data.publicProducts : null
    return list || []
  }, [keptProds.data])

  // The bundle lookup needs a variant to hang off: the first active variant of the
  // first curry. Unknown until the catalog is in, so the query waits.
  const variantId = useMemo(() => {
    const curries = productsInCategory(products, CURRY_CATEGORY)
    for (const p of curries) {
      const v = activeVariants(p)[0]
      if (v) return v.id
    }
    return null
  }, [products])

  const bundles = useQuery<BundlesData>(GET_BUNDLES_FOR_VARIANT, {
    ...poll,
    variables: { variantId: variantId || 0 },
    skip: variantId === null,
  })
  const keptBundles = useKeepLast(bundles.data, bundles.error, variantId === null)

  const bundle = useMemo(() => {
    const list = (keptBundles.data ? keptBundles.data.publicBundlesForVariant : null) || []
    return list.length > 0 ? list[0] : null
  }, [keptBundles.data])

  const ats = [keptCats.at, keptProds.at, keptBundles.at].filter(
    (a): a is number => a !== null,
  )

  return {
    categories: (keptCats.data ? keptCats.data.publicCategories : null) || [],
    products,
    bundle,
    // The oldest confirmed read: the stamp must not promise data fresher than the
    // oldest thing on screen.
    lastSuccessAt: ats.length > 0 ? Math.min.apply(null, ats) : null,
    stale: keptCats.failed || keptProds.failed || keptBundles.failed,
  }
}
